/**
 * EmailMessagesDialog (Phase 7, §API/§Frontend) — the parser-development
 * viewer: a paginated list of one account's stored `email_messages` (newest
 * first, mirrors the backend's `ORDER BY uid DESC`) with parse/outcome/match
 * status, and an on-demand "Rohtext anzeigen" that loads the decoded raw
 * content only for the message the user expands (`useEmailMessageRaw`, the
 * `useReceiptCandidates` on-demand pattern).
 */
import { useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { useEmailMessageRaw, useEmailMessages } from '../../lib/hooks/useEmailConfig.ts';
import { EMAIL_MATCH_STATUS, EMAIL_OUTCOME, EMAIL_PARSE_STATUS } from '../../lib/labels.ts';

const PAGE_SIZE = 20;

export interface EmailMessagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: number;
}

export function EmailMessagesDialog({ open, onOpenChange, accountId }: EmailMessagesDialogProps) {
  const [offset, setOffset] = useState(0);
  const [rawOpenId, setRawOpenId] = useState<number | null>(null);

  const query = useEmailMessages(
    accountId,
    { limit: PAGE_SIZE, offset },
    { enabled: open },
  );

  const items = query.data?.items ?? [];
  const total = query.data?.meta?.total ?? 0;
  const hasPrev = offset > 0;
  const hasNext = offset + items.length < total;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setRawOpenId(null);
      }}
      title="E-Mail-Nachrichten"
      size="lg"
    >
      {query.isPending ? (
        <SkeletonList rows={4} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="Noch keine Nachrichten" description="Es wurden noch keine E-Mails abgerufen." />
      ) : (
        <div className="space-y-4">
          <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
            {items.map((message) => (
              <li key={message.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                      {message.subject ?? '(ohne Betreff)'}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                      {message.from_address ?? '—'}
                      {message.received_at ? ` · ${formatDateTime(message.received_at)}` : ''}
                    </p>
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                      {EMAIL_PARSE_STATUS[message.parse_status]}
                      {' · '}
                      {EMAIL_MATCH_STATUS[message.match_status]}
                      {message.outcome ? ` · ${EMAIL_OUTCOME[message.outcome]}` : ''}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setRawOpenId((cur) => (cur === message.id ? null : message.id))}
                  >
                    {rawOpenId === message.id ? 'Rohtext ausblenden' : 'Rohtext anzeigen'}
                  </Button>
                </div>
                {rawOpenId === message.id ? <RawMessage accountId={accountId} messageId={message.id} /> : null}
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3 dark:border-slate-800/70">
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {total > 0 ? `${offset + 1}–${offset + items.length} von ${total}` : null}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                disabled={!hasPrev}
              >
                Zurück
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                disabled={!hasNext}
              >
                Weiter
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function RawMessage({ accountId, messageId }: { accountId: number; messageId: number }) {
  const raw = useEmailMessageRaw(accountId, messageId);
  return (
    <div className="mt-2 rounded-md bg-slate-50 p-3 dark:bg-slate-800/60">
      {raw.isPending ? (
        <SkeletonList rows={2} />
      ) : raw.isError ? (
        <ErrorState error={raw.error} onRetry={() => void raw.refetch()} />
      ) : (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-slate-600 dark:text-slate-300">
          {raw.data?.raw_text}
        </pre>
      )}
    </div>
  );
}
